/* eslint-disable @typescript-eslint/no-unused-vars */
import Konva from 'konva';
import { IRect } from 'konva/lib/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image as KonvaReactImage } from 'react-konva';
import { DebuggingImageData } from './DebuggingImageData';
import { ImgProps } from '../types';
import useRelativeSize from '../hooks/useRelativeSize';
import useURLImage from '../hooks/useURLImage';
import { containWithinFaceplateBoundingBox } from '../utils';

export const Img = ({
    id,
    src,
    width: suggestedWidth,
    height: suggestedHeight,
    onLoad,
    onPosInitialized,
    // containWithin, // wip
    clipOrigin,
    clipCenter,
    faceplateBbox,
    collisionMargin,
    debugBoundaries,
    onTransform,
    onDragMove,
    onDragEnd,
    onTransformEnd,
    // handleChange,
    rotation: suggestedRotation,
    ...props
}: ImgProps) => {
    const { image: imgBlob } = useURLImage({ src });
    const ref = useRef<Konva.Image>();
    const prevSrcRef = useRef<string>(src);
    const isSrcChanged = prevSrcRef.current !== src;
    const [imageRect, setImageRect] = useState<IRect>();
    const { width, height } = useRelativeSize({
        width: (isSrcChanged ? 0 : suggestedWidth) || imgBlob?.width || 0,
        height: (isSrcChanged ? 0 : suggestedHeight) || imgBlob?.height || 0,
        maxWidth: props.maxWidth
    });

    // const handleChange = (e: Konva.KonvaEventObject<DragEvent>) => {
    const handleChange = (e: Konva.KonvaEventObject<Event>) => {
        if (!faceplateBbox) return;
        const node = e.target;
        const rect = node.getClientRect({ relativeTo: node.getParent() });
        setImageRect(rect);
        containWithinFaceplateBoundingBox({
            rect,
            faceplateBbox,
            clipOrigin,
            collisionMargin,
            node
        });
    };

    const { initialX, initialY } = useMemo(() => {
        const res = { initialX: props.x || 0, initialY: props.y || 0 };
        if (props.x || props.y || !ref.current) {
            return res;
        }
        const node = ref.current;
        const rect = node.getClientRect({ relativeTo: node.getParent() });
        if (props.x === undefined && clipCenter && rect) {
            res.initialX = clipCenter[0];
            //    - rect.width / 2;
        }
        if (props.y === undefined && clipCenter && rect) {
            res.initialY = clipCenter[1];
            //    - rect.height / 2;
        }

        return res;
    }, [ref.current, clipCenter, width, height, props.x, props.y, suggestedRotation]);

    useEffect(() => {
        if (initialX && initialY && onPosInitialized) {
            onPosInitialized(
                {
                    id,
                    src,
                    ...ref.current?.attrs,
                    x: initialX,
                    y: initialY
                },
                ref.current
            );
        }
    }, [initialX, initialY]);

    useEffect(() => {
        if (!imgBlob || !ref.current) return;
        if (prevSrcRef.current === src) return;
        prevSrcRef.current = src;

        const node = ref.current;
        node.scaleX(1);
        node.scaleY(1);

        const rect = node.getClientRect({ relativeTo: node.getParent() });
        setImageRect(rect);

        if (onPosInitialized) {
            onPosInitialized(
                {
                    id,
                    src,
                    ...node.attrs,
                    x: node.x(),
                    y: node.y()
                },
                node
            );
        }
    }, [imgBlob]);

    useEffect(() => {
        if (imgBlob && onLoad) {
            onLoad(imgBlob);
        }
    }, [imgBlob]);

    return width && height ? (
        <>
            {debugBoundaries && imageRect && (
                <DebuggingImageData
                    bBox={imageRect}
                    clipCenter={clipCenter}
                    clipOrigin={clipOrigin}
                    faceplateBbox={faceplateBbox}
                />
            )}
            <KonvaReactImage
                image={imgBlob}
                crop={{
                    x: 0,
                    y: 0,
                    width,
                    height
                }}
                width={width}
                height={height}
                // @ts-ignore
                globalCompositeOperation={props.blendMode ? props?.composition : ''}
                onTransform={(event) => {
                    handleChange(event);
                    onTransform(event);
                }}
                onDragMove={(event) => {
                    handleChange(event);
                    onDragMove(event);
                }}
                onTransformEnd={(e) => {
                    if (!onTransformEnd) return;
                    onTransformEnd({
                        id,
                        src,
                        ...e.target.attrs
                    });
                }}
                onDragEnd={(e) => {
                    if (!onDragEnd) return;
                    onDragEnd({
                        id,
                        src,
                        ...e.target.attrs
                    });
                }}
                rotation={suggestedRotation}
                {...props}
                {...(isSrcChanged ? { scaleX: 1, scaleY: 1 } : {})}
                x={props.x || initialX}
                y={props.y || initialY}
                ref={(r: Konva.Image) => {
                    if (r && !ref.current) {
                        ref.current = r;
                        const rect = r.getClientRect({ relativeTo: r.getParent() });
                        setImageRect(rect);
                    }
                }}
            />
        </>
    ) : null;
};
